import { useEffect, useState } from 'react';
import styles from './Base.module.css';
import LoginWidget from '../widgets/LoginWidget';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { getAppLogoSrc } from '../utils/appBranding';
import api from '../API';
import { girderThumbnailUrl } from '../utils/girderThumbnailUrl';
import {
    ProjectChromeBrandingProvider,
    ProjectChromeBrandingSync,
} from '../providers/ProjectChromeBrandingContext';
import { useOrgWorkspaceNav } from '../providers/useOrgWorkspaceNav';

function TopBar() {
    const {
        chromeTitle,
        chromeHomeTo,
        logoItemId,
        isOrgWorkspace,
        projectsRootTo,
    } = useOrgWorkspaceNav();
    const [logoFailed, setLogoFailed] = useState(false);

    useEffect(() => {
        setLogoFailed(false);
    }, [logoItemId]);

    const customLogoSrc = logoItemId && !logoFailed
        ? girderThumbnailUrl(api, logoItemId, { width: 64, height: 64 })
        : null;
    const logo = customLogoSrc || getAppLogoSrc();

    return (
        <div className={styles.topbar}>
            <div className={styles.topbarBrand}>
                {isOrgWorkspace ? (
                    <NavLink
                        to={projectsRootTo}
                        className={styles.topbarProjectsRoot}
                        aria-label="Back to projects"
                        title="Back to projects"
                    >
                        ‹
                    </NavLink>
                ) : null}
                <NavLink to={chromeHomeTo} className={styles.topbarLogoLink}>
                    <img
                        src={logo}
                        alt=""
                        onError={() => setLogoFailed(true)}
                    />
                </NavLink>
                <NavLink to={chromeHomeTo} className={styles.homelink}>{chromeTitle}</NavLink>
            </div>
            <LoginWidget></LoginWidget>
        </div>
    );
}

// if content is passed in as a parameter, it will be used in place of the normal Outlet
// this allows Base to be reused for error pages e.g. when a user does not have permissions
function Base({content}){
    return(
        <ProjectChromeBrandingProvider>
            <ProjectChromeBrandingSync />
            <div className={styles.fullpage}>
                <TopBar />
                <div className={styles.app}>
                    {!content && <Outlet />}
                    {content&&content}
                </div>
            </div>
        </ProjectChromeBrandingProvider>
    )
}

function NoTopbar({content}){
    console.log('NoTopbar function returning Outlet')
    return (<Outlet></Outlet>)
    // return content ? content : (<Outlet></Outlet>)
}

export default Base;

function Centered({ children }){
    return (
        <>
        <div className={styles.centeredWrapper}>
            <div className={styles.centered}>
                {children}
            </div>
        </div>
        </>
    )
}

/**
 * navLinks: array of { text, to?, end?, disabled?, title?, sectionId?, children? }.
 * Use end: true on a hub route (e.g. /projects) so nested routes do not stay active.
 * children (optional): array of same shape { text, to, disabled? }, rendered as indented sub-items.
 *
 * Always wraps `children` in the same main-content slot so toggling the sidebar
 * (empty vs non-empty navLinks) does not remount route pages.
 */
function LeftNav({ children, navLinks = [], collapsedLabel = 'Menu' }) {
    const hasSidebar = Array.isArray(navLinks) && navLinks.length > 0;
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('bdsa-left-nav-collapsed');
            if (saved === '1') return true;
            if (saved === '0') return false;
        } catch {
            // ignore
        }
        return location.pathname.includes('/wrangler');
    });

    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('bdsa-left-nav-collapsed', next ? '1' : '0');
            } catch {
                // ignore
            }
            return next;
        });
    };

    const renderLink = (def, key) => {
        if (def.sectionId) {
            return (
                <a
                    href={`#${def.sectionId}`}
                    className={`${styles.leftNavLink} ${styles.leftNavSectionLink}`}
                    onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(def.sectionId)?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }}
                >
                    {def.text}
                </a>
            );
        }
        if (def.disabled) {
            return (
                <span className={styles.leftNavLink} style={{ opacity: 0.5, cursor: 'not-allowed', color: '#999' }}>
                    {def.text}
                </span>
            );
        }
        return (
            <NavLink
                to={def.to}
                end={def.end ?? false}
                className={({ isActive }) =>
                    `${styles.leftNavLink} ${isActive ? styles.leftNavLinkActive : ''}`
                }
            >
                {def.text}
            </NavLink>
        );
    };

    const makeLink = (def, i) => {
        const hasChildren = Array.isArray(def.children) && def.children.length > 0;
        return (
            <li key={i} className={def.sectionNavStart ? styles.leftNavSectionStart : undefined}>
                {renderLink(def, i)}
                {hasChildren && (
                    <ul className={styles.leftNavSubList}>
                        {def.children.map((child, j) => (
                            <li key={child.to ?? j} className={styles.leftNavSubItem}>
                                {child.disabled ? (
                                    <span className={styles.leftNavLink} style={{ opacity: 0.5, cursor: 'not-allowed', color: '#999' }}>
                                        {child.text}
                                    </span>
                                ) : (
                                    <NavLink
                                        to={child.to}
                                        end={child.end ?? false}
                                        className={({ isActive }) =>
                                            `${styles.leftNavLink} ${isActive ? styles.leftNavLinkActive : ''}`
                                        }
                                    >
                                        {child.text}
                                    </NavLink>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <div className={
            hasSidebar
                ? `${styles.leftNavLayout}${collapsed ? ` ${styles.leftNavLayoutCollapsed}` : ''}`
                : styles.leftNavLayoutNoSidebar
        }>
            {hasSidebar ? (
                <div className={`${styles.leftNav}${collapsed ? ` ${styles.leftNavCollapsed}` : ''}`}>
                    {collapsed ? (
                        <button
                            type="button"
                            className={styles.leftNavRail}
                            onClick={toggleCollapsed}
                            aria-expanded={false}
                            aria-label={`Expand ${collapsedLabel} sidebar`}
                            title={`Expand ${collapsedLabel} sidebar`}
                        >
                            <span className={styles.leftNavToggle} aria-hidden>›</span>
                            <span className={styles.leftNavRailLabel}>{collapsedLabel}</span>
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={styles.leftNavToggle}
                                onClick={toggleCollapsed}
                                aria-expanded={true}
                                aria-label={`Collapse ${collapsedLabel} sidebar`}
                                title={`Collapse ${collapsedLabel} sidebar`}
                            >
                                ‹
                            </button>
                            <ul>
                                {navLinks.map(makeLink)}
                            </ul>
                        </>
                    )}
                </div>
            ) : null}
            <div className={styles.leftNavMain}>
                {children}
            </div>
        </div>
    );
}


export { Base, NoTopbar, Centered, LeftNav };
